// react
import { useCallback, useMemo } from 'react';

// components
import useStableSearchParams from '@/hooks/useStableSearchParams';

// relative
import type { SeriesVisibility } from '../types';

const HIDDEN_SERIES_PARAM = 'hiddenSeries';
const VISIBLE_SERIES_PARAM = 'visibleSeries';

interface SeriesVisibilityUrlState {
  hiddenIds: string[];
  visibleIds: string[];
}

function parseSeriesIds(value: string | null): string[] {
  if (!value) return [];
  return value
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);
}

function toSeriesVisibility({
  hiddenIds,
  visibleIds,
}: SeriesVisibilityUrlState): SeriesVisibility {
  const visibility = hiddenIds.reduce<SeriesVisibility>((acc, id) => {
    acc[id] = { visible: false };
    return acc;
  }, {});

  visibleIds.forEach((id) => {
    visibility[id] = { visible: true };
  });

  return visibility;
}

function normalizeSeriesIds(ids: string[]): string[] {
  return Array.from(new Set(ids.filter(Boolean))).sort();
}

function useSeriesVisibilityUrl(): [
  SeriesVisibility,
  (state: SeriesVisibilityUrlState) => void,
] {
  const [searchParams, setSearchParams] = useStableSearchParams();

  const hiddenSeriesIds = useMemo(
    () => parseSeriesIds(searchParams.get(HIDDEN_SERIES_PARAM)),
    [searchParams],
  );

  const visibleSeriesIds = useMemo(
    () => parseSeriesIds(searchParams.get(VISIBLE_SERIES_PARAM)),
    [searchParams],
  );

  const seriesVisibility = useMemo(
    () =>
      toSeriesVisibility({
        hiddenIds: hiddenSeriesIds,
        visibleIds: visibleSeriesIds,
      }),
    [hiddenSeriesIds, visibleSeriesIds],
  );

  const setSeriesVisibilityInUrl = useCallback(
    ({ hiddenIds, visibleIds }: SeriesVisibilityUrlState) => {
      const uniqueVisibleIds = normalizeSeriesIds(visibleIds);
      const visibleSet = new Set(uniqueVisibleIds);
      const uniqueHiddenIds = normalizeSeriesIds(hiddenIds).filter(
        (id) => !visibleSet.has(id),
      );

      setSearchParams(
        (prev: URLSearchParams) => {
          const nextParams = new URLSearchParams(prev);

          if (uniqueHiddenIds.length > 0) {
            nextParams.set(HIDDEN_SERIES_PARAM, uniqueHiddenIds.join(','));
          } else {
            nextParams.delete(HIDDEN_SERIES_PARAM);
          }

          if (uniqueVisibleIds.length > 0) {
            nextParams.set(VISIBLE_SERIES_PARAM, uniqueVisibleIds.join(','));
          } else {
            nextParams.delete(VISIBLE_SERIES_PARAM);
          }

          return nextParams;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return [seriesVisibility, setSeriesVisibilityInUrl];
}

export default useSeriesVisibilityUrl;
